// Корректные MIME-типы. Ранее в коде использовались несуществующие
// 'application/xlsx' и 'application/docx' — WebKit на них ведёт себя непредсказуемо.
export const MIME = {
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  PDF: 'application/pdf',
  CSV: 'text/csv',
  DBF: 'application/octet-stream',
  // OSI-845: ZIP-архив отдельных квитанций (каждая квартира — отдельный PDF)
  ZIP: 'application/zip',
  BIN: 'application/octet-stream'
} as const;

/**
 * MIME по голому расширению (без точки, регистронезависимо).
 * Предпочтительный вариант, когда расширение хранится ОТДЕЛЬНЫМ полем (doc.extension):
 * fileName в таких моделях может прийти без расширения.
 * Неизвестное расширение → octet-stream (браузер просто сохранит файл).
 */
export const mimeByExtension = (ext?: string): string => {
  switch (ext?.toLowerCase() ?? '') {
    case 'xlsx':
      return MIME.XLSX;
    case 'docx':
      return MIME.DOCX;
    case 'pdf':
      return MIME.PDF;
    case 'csv':
      return MIME.CSV;
    case 'dbf':
      return MIME.DBF;
    // OSI-845: архив отдельных квитанций
    case 'zip':
      return MIME.ZIP;
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    default:
      return MIME.BIN;
  }
};

/**
 * MIME по расширению из имени файла — для сканов документов, у которых отдельного поля
 * с расширением нет (есть только scan.fileName).
 */
export const mimeByFileName = (fileName?: string): string => mimeByExtension(fileName?.split('.').pop());

/** OSI-845: формат бинарного ответа, определённый по сигнатуре (первым байтам). */
export type BinaryFileFormat = 'pdf' | 'zip';

/**
 * OSI-845: определяет формат файла по первым байтам ответа: 'PK' (0x50 0x4B) → zip, иначе pdf.
 * Нужен, т.к. сервер сам решает, отдать PDF или ZIP, а заголовки ответа (Content-Type,
 * Content-Disposition) до вызывающего кода не доходят — перехватчики axios возвращают только data.
 */
export const detectBinaryFormat = async (data: Blob | ArrayBuffer): Promise<BinaryFileFormat> => {
  const head = data instanceof Blob ? await data.slice(0, 2).arrayBuffer() : data.slice(0, 2);
  const bytes = new Uint8Array(head);
  return bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b ? 'zip' : 'pdf';
};

/** base64-строка -> Blob. Нужен, т.к. бэкенд отдаёт файлы в JSON как base64. */
export const base64ToBlob = (base64: string, mime: string): Blob => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

/**
 * Скачивание Blob через blob:-URL.
 * Почему именно так (iOS/WebKit, Safari и Chrome на iOS — один движок):
 *  - blob:-URL вместо data:-URL — WebKit не скачивает data:-URL по атрибуту download
 *    и имеет лимит на его длину (крупные отчёты не проходили);
 *  - якорь ОБЯЗАН быть в DOM до click() — по detached-элементу WebKit клик игнорирует;
 *  - удаление узла и revokeObjectURL — только через setTimeout, иначе WebKit
 *    успевает отменить уже начатую загрузку.
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 250);
};

/** Скачивание файла, пришедшего с бэкенда в виде base64-строки. */
export const downloadBase64 = (base64: string, mime: string, fileName: string): void =>
  downloadBlob(base64ToBlob(base64, mime), fileName);

/**
 * OSI-845: скачивание бинарного ответа «PDF или ZIP» с корректным MIME и расширением.
 * baseName — имя без расширения; расширение (.pdf/.zip) подставляется по сигнатуре ответа.
 * Скачивание через downloadBlob (blob:-URL, якорь в DOM) — корректно работает в iOS/WebKit.
 */
export const downloadPdfOrZip = async (data: Blob | ArrayBuffer, baseName: string): Promise<void> => {
  const format = await detectBinaryFormat(data);
  downloadBlob(new Blob([data], { type: mimeByExtension(format) }), `${baseName}.${format}`);
};
